// Home.tsx - Redesigned with animations and parallax effects
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/indexStore";
import { Constants } from "../Constants";
import groupBy from "../components/admin/util/groupBy";
import MiniCoaster from "../components/mini_coaster/MiniCoaster";
import { motion, MotionValue, useScroll, useTransform } from "framer-motion";
import "./Home.css";

// Custom hook for scroll animations
function useParallax(value: MotionValue<number>, distance: number): MotionValue<number> {
  return useTransform(value, [0, 1], [-distance, distance]);
}

const Home = () => {
  const { scrollYProgress } = useScroll();
  const default_content = useSelector((state: RootState) => state.eduContent);
  const [isLoaded, setIsLoaded] = useState(false);
  const heroRef = useRef(null);

  let grouped = groupBy(Constants.DEFAULT_EDU_CONTENT.filter(content => content.display), "eduheader");

  if (default_content.length > 1) {
    grouped = groupBy(default_content.filter(content => content.display), "eduheader");
  }

  // Sorting to ensure Referral to Hospitals appears first
  const orderedSections = [
    ...Object.keys(grouped).filter(x => grouped[x][0].head_title === "Referral to Hospitals"),
    ...Object.keys(grouped).filter(x => grouped[x][0].head_title !== "Referral to Hospitals")
  ];

  // Create parallax values at the top level, not inside a callback
  const yHeaderParallax = useParallax(scrollYProgress, 100);
  const heroBgParallax = useParallax(scrollYProgress, -150);
  
  // Pre-create parallax values for each section (up to a reasonable maximum)
  // This avoids calling hooks inside loops or conditionals
  const parallax0 = useParallax(scrollYProgress, 0);
  const parallax1 = useParallax(scrollYProgress, 20);
  const parallax2 = useParallax(scrollYProgress, 40);
  const parallax3 = useParallax(scrollYProgress, 60);
  const parallax4 = useParallax(scrollYProgress, 80);
  const parallax5 = useParallax(scrollYProgress, 100);
  const parallax6 = useParallax(scrollYProgress, 120);
  const parallax7 = useParallax(scrollYProgress, 140);
  
  // Create an array of the pre-defined parallax values
  const parallaxValues = [
    parallax0, parallax1, parallax2, parallax3, 
    parallax4, parallax5, parallax6, parallax7
  ];

  useEffect(() => {
    // This will help identify where the gap is coming from
    const findGap = () => {
      console.log('Debugging layout gap issue...');
      
      // Get all top-level elements
      const body = document.body;
      const allElements = Array.from(document.querySelectorAll('body > *'));
      
      console.log('Body margin/padding:', window.getComputedStyle(body).margin, window.getComputedStyle(body).padding);
      
      // Check hero container and its parents
      const heroContainer = document.querySelector('.hero-container');
      // [... rest of the code in the artifact]
    };
    
    setTimeout(findGap, 500);
  }, []);

  useEffect(() => {
    setIsLoaded(true);
    
    // Add scroll-triggered animations to elements with specific classes
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, { threshold: 0.1 });
    
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
    
    <motion.section 
      className="dental-health-hero relative flex flex-col items-center justify-center py-12 px-4 bg-gradient-to-b from-blue-50 to-white"
      initial="hidden"
      animate={isLoaded ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
      }}
    >
        <div className="dental-health-container max-w-4xl mx-auto text-center">
    {/* Enhanced mascot animation */}
    <motion.div
  className="mb-6 flex justify-center"
  animate={{ y: [0, -8, 0], scale: [1, 1.03, 1] }}
  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
>
  <div className="relative">
    {/* Smoothing outline */}
    <div className="absolute inset-0 bg-blue-100 rounded-full blur-[12px] opacity-50 -z-10 scale-[1.05]" />
    
    {/* Image with enhancements */}
    <img 
  src="https://sgtb-pic-store.s3.ap-southeast-1.amazonaws.com/Dencap-mascot.png"
  alt="ToothBuddy"
      className="object-contain filter drop-shadow-md contrast-105"
      style={{ width: '150px', height: '150px' }}
    />
  </div>
</motion.div>

        {/* Animated title */}
        <motion.h2
          className="dental-health-title text-3xl md:text-4xl font-bold text-blue-600 mb-4"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 }
          }}
        >
          ToothBuddy, your SAF dental pal!
        </motion.h2>

        {/* Animated description */}
        <motion.p
          className="dental-health-description text-lg text-gray-700 max-w-2xl mx-auto"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0 }
          }}
        >
          ToothBuddy helps you with answers to FAQs, and tips on preventing and treating oral health issues throughout your SAF service and beyond.
        </motion.p>
      </div>
    </motion.section>

      <div className="containerHome">
        {orderedSections.map((k, sectionIndex) => {
          // Use the pre-created parallax values, or fallback to the first one if index is out of bounds
          const parallax = sectionIndex < parallaxValues.length ? 
            parallaxValues[sectionIndex] : parallaxValues[0];
            
          return (
            <motion.div 
              key={grouped[k][0].head_title}
              className="section-container animate-on-scroll"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: sectionIndex * 0.1 }}
            >
              <motion.h1 
                className="line"
                style={{ y: parallax }}
              >
                {grouped[k][0].head_title}
              </motion.h1>
              <div className="subcontainerHome">
                {grouped[k].map((e, i) => (
                  <motion.div
                    key={e.title}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      duration: 0.5, 
                      delay: i * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <MiniCoaster title={e.title} picURL={e.pic_url} pathVar={e.path_var} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Features section with animated icons */}
      <div className="features-section">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Committed to Your Oral Health
        </motion.h2>
        
        <div className="features-grid">
          {[
{ 
  icon: "🦷", 
  title: "Comprehensive Dental Care", 
  description: "As part of your SAF benefits, enjoy free dental treatments at SAF Dental Clinics, ensuring your oral health is always a priority." 
},
{ 
  icon: "🛡️", 
  title: "Preventive Services", 
  description: "Our focus is on preventive care, helping you avoid common dental issues and ensuring long-term health for all servicemen." 
},
{ 
  icon: "🌱", 
  title: "Health Beyond Dentistry", 
  description: "We care about your overall well-being, combining dental care with lifestyle advice to support your health in and out of service." 
},
{ 
  icon: "⚕️", 
  title: "Advanced Treatment", 
  description: "Leverage state-of-the-art technology and treatments covered under your medical benefits, ensuring fast recovery and high-quality care." 
}
          ].map((feature, index) => (
            <motion.div 
              key={index}
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -10, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" }}
            >
              <motion.div 
                className="feature-icon"
                animate={{ 
                  rotateY: [0, 360],
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  rotateY: { repeat: Infinity, duration: 6, ease: "linear" },
                  scale: { repeat: Infinity, duration: 3 }
                }}
              >
                {feature.icon}
              </motion.div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

     
    </>
  );
};

// Simple testimonial carousel component
const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const testimonials = [
    {
      name: "Sarah Johnson",
      text: "The best dental experience I've ever had! Dr. Smith made my wisdom tooth extraction completely painless.",
      rating: 5
    },
    {
      name: "Michael Chen",
      text: "Finally found a dentist who takes the time to explain everything. My kids love coming here too!",
      rating: 5
    },
    {
      name: "Maria Rodriguez",
      text: "Their preventative care program saved me from needing a root canal. I'm so grateful for their expertise.",
      rating: 4
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="testimonial-carousel">
      {testimonials.map((testimonial, index) => (
        <motion.div 
          key={index}
          className="testimonial-card"
          initial={{ opacity: 0, x: 100 }}
          animate={{ 
            opacity: currentIndex === index ? 1 : 0,
            x: currentIndex === index ? 0 : 100,
            display: currentIndex === index ? "block" : "none"
          }}
          transition={{ duration: 0.5 }}
        >
          <div className="rating">
            {Array(testimonial.rating).fill("⭐").join("")}
          </div>
          <p className="testimonial-text">"{testimonial.text}"</p>
          <p className="testimonial-author">- {testimonial.name}</p>
        </motion.div>
      ))}
      <div className="carousel-indicators">
        {testimonials.map((_, index) => (
          <motion.button
            key={index}
            className={`indicator ${currentIndex === index ? "active" : ""}`}
            onClick={() => setCurrentIndex(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </div>
    </div>
  );
};





export default Home;